import React, { useEffect } from 'react';
import { ToggleButton, ToggleButtonGroup, ToggleButtonGroupProps, Typography } from '@mui/material';
import { useField, useFormState } from 'react-final-form';
import Column from './Column';

type ToggleValue = string | string[];

export interface ToggleInputProps<T> extends ToggleButtonGroupProps {
  inputStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  fieldName: keyof T;
  items: string[];
  initialValue?: ToggleValue;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  exclusive?: boolean;
  className?: string;
  validate?: (x: any) => undefined | string;
  handleToggleChange?: (x: any) => void;
}

const ToggleInput = <T,>({
  inputStyle,
  buttonStyle,
  fieldName,
  items,
  initialValue,
  required,
  disabled,
  readonly,
  exclusive = true,
  className,
  validate,
  fullWidth,
  handleToggleChange,
}: ToggleInputProps<T>) => {
  const { submitFailed } = useFormState({ subscription: { submitFailed: true } });

  const { input, meta } = useField<ToggleValue>(fieldName.toString(), {
    initialValue,
    validate: (value: ToggleValue) => {
      if (required) {
        const missing = exclusive
          ? typeof value !== 'string' || value.length === 0
          : !Array.isArray(value) || value.length === 0;

        if (missing) return 'Required';
      }

      return validate ? validate(value) : undefined;
    },
    subscription: { value: true, touched: true, error: true },
  });

  useEffect(() => {
    if (handleToggleChange) handleToggleChange(input.value);
  }, [input.value, handleToggleChange]);

  const handleChange: ToggleButtonGroupProps['onChange'] = (_, newValue) => {
    if (exclusive) {
      if (newValue !== null) input.onChange(newValue);
      return;
    }

    input.onChange(newValue ?? []);
  };

  const groupValue = exclusive
    ? typeof input.value === 'string'
      ? input.value
      : ''
    : Array.isArray(input.value)
      ? input.value
      : [];

  const showError = meta.error !== undefined && (meta.touched || submitFailed);

  const columnStyles: React.CSSProperties = {
    ...(fullWidth && { width: '100%' }),
    ...inputStyle,
  };

  return (
    <Column style={{ position: 'relative', ...columnStyles }}>
      <ToggleButtonGroup
        className={showError ? 'has-error' : undefined}
        fullWidth={fullWidth}
        style={inputStyle}
        value={groupValue}
        exclusive={exclusive}
        onChange={handleChange}
        disabled={disabled || readonly}>
        {items.map((item, index) => (
          <ToggleButton
            key={`${item}-${index}`}
            className={className}
            style={{ flexGrow: 3, ...buttonStyle }}
            value={item}>
            {item}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {showError && (
        <Typography
          style={{ fontSize: '.75rem', marginTop: '3px', position: 'absolute', top: '100%' }}
          color='error.main'>
          {meta.error}
        </Typography>
      )}
    </Column>
  );
};

export default ToggleInput;
